import { useState, useEffect, useRef } from "react";
import { 
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Calendar, Loader2, RefreshCw, Download, FileSpreadsheet, RefreshCcw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const currencySymbols = {
  USD: "$",
  LRD: "L$",
  EUR: "€",
  GBP: "£",
  INR: "₹",
  KES: "KSh",
  NGN: "₦",
  GHS: "GH₵",
  ZAR: "R",
  JPY: "¥",
  CNY: "¥",
  XOF: "CFA",
  CAD: "C$",
  AUD: "A$"
};

export default function CashFlowStatement() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const reportRef = useRef(null);

  // Get currencies from business profile
  const transactionCurrency = businessProfile?.default_currency || "USD";
  const reportingCurrency = businessProfile?.reporting_currency || transactionCurrency;
  const needsConversion = transactionCurrency !== reportingCurrency;

  useEffect(() => {
    if (period !== "custom") {
      loadData();
    } else {
      setLoading(false);
    }
    loadBusinessProfile();
  }, [period]);

  useEffect(() => {
    // Load exchange rate when currencies are different
    if (businessProfile && needsConversion) {
      loadExchangeRate();
    }
  }, [businessProfile, transactionCurrency, reportingCurrency]);

  const loadBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      setBusinessProfile(response.data);
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  const loadExchangeRate = async () => {
    if (!needsConversion) return;
    setLoadingRate(true);
    try {
      const response = await axios.get(
        `${API}/exchange-rate?from_currency=${transactionCurrency}&to_currency=${reportingCurrency}`
      );
      setExchangeRate(response.data);
    } catch (error) {
      console.error("Failed to load exchange rate:", error);
      toast.error("Failed to load exchange rate");
    } finally {
      setLoadingRate(false);
    }
  };

  const refreshExchangeRate = async () => {
    if (!needsConversion) return;
    setLoadingRate(true);
    try {
      const response = await axios.post(
        `${API}/exchange-rate/refresh?from_currency=${transactionCurrency}&to_currency=${reportingCurrency}`
      );
      setExchangeRate({
        from_currency: transactionCurrency,
        to_currency: reportingCurrency,
        rate: response.data.rate,
        updated_at: new Date().toISOString()
      });
      toast.success("Exchange rate refreshed!");
    } catch (error) {
      console.error("Failed to refresh exchange rate:", error);
      toast.error("Failed to refresh exchange rate");
    } finally {
      setLoadingRate(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let url = `${API}/cash/summary?period=${period}`;
      if (period === "custom" && startDate && endDate) {
        url = `${API}/cash/summary?start_date=${startDate}&end_date=${endDate}`;
      }
      
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error("Failed to load cash flow data:", error);
      toast.error("Failed to load cash flow statement");
    } finally {
      setLoading(false);
    }
  };

  // Convert amount using exchange rate if needed
  const convertAmount = (amount) => {
    if (!needsConversion || !exchangeRate) return amount || 0;
    return (amount || 0) * exchangeRate.rate;
  };

  const formatCurrency = (amount, applyConversion = true) => {
    const displayCurrency = applyConversion && needsConversion ? reportingCurrency : transactionCurrency;
    const displayAmount = applyConversion ? convertAmount(amount) : amount;
    const symbol = currencySymbols[displayCurrency] || displayCurrency;
    
    return `${symbol}${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(displayAmount || 0)}`;
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`cash-flow-statement-${data?.start_date || "report"}.pdf`);
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="cash-flow-statement">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Cash Flow Statement</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {data?.start_date} to {data?.end_date}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="period-select">
              <Calendar className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {period === "custom" && (
            <>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36 h-9"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36 h-9"
              />
              <Button 
                variant="default" 
                size="sm" 
                onClick={loadData} 
                disabled={loading || !startDate || !endDate} 
                className="h-9"
                data-testid="apply-custom-range-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
            </>
          )}
          
          <Button variant="ghost" size="sm" onClick={loadData} disabled={loading} className="h-9 w-9 p-0">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportToPDF} 
            disabled={exporting || !data} 
            className="h-9"
          >
            <Download className="w-4 h-4 mr-1.5" />
            PDF
          </Button>
        </div>
      </div>

      {/* Exchange Rate Banner */}
      {needsConversion && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">{currencySymbols[transactionCurrency]}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900">
                Currency Conversion Applied
              </p>
              <p className="text-xs text-blue-700">
                1 {transactionCurrency} = {exchangeRate?.rate?.toFixed(2) || "..."} {reportingCurrency}
                {exchangeRate?.updated_at && (
                  <span className="text-blue-500 ml-2">
                    (Updated: {new Date(exchangeRate.updated_at).toLocaleDateString()})
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshExchangeRate}
            disabled={loadingRate}
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            {loadingRate ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <RefreshCcw className="w-4 h-4 mr-1" />
                Refresh Rate
              </>
            )}
          </Button>
        </div>
      )}

      {/* Statement */}
      <div 
        ref={reportRef}
        className="bg-white border rounded-lg shadow-sm overflow-hidden"
      >
        {/* Top Brand Bar */}
        <div 
          className="h-2"
          style={{ backgroundColor: businessProfile?.brand_primary_color || "#10b981" }}
        />
        
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8 border-b pb-6">
            {/* Logo */}
            {(businessProfile?.logo_data || businessProfile?.logo_url) && (
              <img 
                src={businessProfile.logo_data || businessProfile.logo_url}
                alt="Logo"
                className="h-16 mx-auto mb-3 object-contain"
              />
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {businessProfile?.business_name || "Your Business"}
            </h2>
            {businessProfile?.address && (
              <p className="text-sm text-gray-500">{businessProfile.address}</p>
            )}
            <h3 
              className="text-lg font-semibold mt-4"
              style={{ color: businessProfile?.brand_primary_color || "#10b981" }}
            >
              CASH FLOW STATEMENT
              {needsConversion && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (Reported in {reportingCurrency})
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              For the period {data?.start_date} to {data?.end_date}
            </p>
          </div>

          {/* Operating Activities */}
          <div className="mb-8">
            <h4 
              className="font-semibold text-white px-4 py-2 rounded"
              style={{ backgroundColor: businessProfile?.brand_primary_color || "#10b981" }}
            >
              Cash Flows from Operating Activities
            </h4>
            
            {/* Cash Inflows */}
            <div className="mt-4 px-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Cash Inflows:</p>
              {data?.inflows_by_category && Object.entries(data.inflows_by_category).length > 0 ? (
                Object.entries(data.inflows_by_category).map(([category, amount]) => (
                  <div key={category} className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 pl-4">{category}</span>
                    <span className="text-emerald-600">{formatCurrency(amount)}</span>
                  </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 pl-4">No inflows</p>
            )}
            <div className="flex justify-between py-2 border-t mt-2 font-medium">
              <span className="text-gray-700">Total Cash Inflows</span>
              <span className="text-emerald-600">{formatCurrency(data?.total_inflows)}</span>
            </div>
          </div>
          
          {/* Cash Outflows */}
          <div className="mt-4 px-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Cash Outflows:</p>
            {data?.outflows_by_category && Object.entries(data.outflows_by_category).length > 0 ? (
              Object.entries(data.outflows_by_category).map(([category, amount]) => (
                <div key={category} className="flex justify-between py-1 text-sm">
                  <span className="text-gray-600 pl-4">{category}</span>
                  <span className="text-red-600">({formatCurrency(amount)})</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 pl-4">No outflows</p>
            )}
            <div className="flex justify-between py-2 border-t mt-2 font-medium">
              <span className="text-gray-700">Total Cash Outflows</span>
              <span className="text-red-600">({formatCurrency(data?.total_outflows)})</span>
            </div>
          </div>
          
          {/* Net Cash from Operating */}
          <div 
            className="flex justify-between py-3 px-4 rounded mt-4 font-semibold"
            style={{ backgroundColor: `${businessProfile?.brand_primary_color || "#10b981"}15` }}
          >
            <span className="text-gray-800">Net Cash from Operating Activities</span>
            <span className={data?.net_cash_flow >= 0 ? "text-emerald-600" : "text-red-600"}>
              {formatCurrency(data?.net_cash_flow)}
            </span>
          </div>
        </div>

        {/* Summary */}
        <div className="border-t pt-6">
          <h4 
            className="font-semibold text-white px-4 py-2 rounded mb-4"
            style={{ backgroundColor: businessProfile?.brand_primary_color || "#10b981" }}
          >
            Cash Position Summary
          </h4>
          
          <div className="space-y-2 px-4">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Beginning Cash Balance</span>
              <span className="font-medium">{formatCurrency(data?.opening_balance)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Net Change in Cash</span>
              <span className={`font-medium ${data?.net_cash_flow >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {data?.net_cash_flow >= 0 ? "+" : ""}{formatCurrency(data?.net_cash_flow)}
              </span>
            </div>
            <div 
              className="flex justify-between py-3 border-t-2 font-bold text-lg"
              style={{ borderColor: businessProfile?.brand_primary_color || "#10b981" }}
            >
              <span className="text-gray-900">Ending Cash Balance</span>
              <span style={{ color: businessProfile?.brand_primary_color || "#10b981" }}>
                {formatCurrency(data?.closing_balance)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t text-center text-xs text-gray-400">
          <p>Generated by LedgerAMT • {new Date().toLocaleDateString()}</p>
        </div>
        </div>
        
        {/* Bottom Brand Bar */}
        <div 
          className="h-1.5"
          style={{ backgroundColor: businessProfile?.brand_primary_color || "#10b981" }}
        />
      </div>
    </div>
  );
}
